import * as React from 'react';
import { 
    AppliedFilterBarOpt, 
    FILTER_BAR_TYPE, 
    FilterBarInputOpt, 
    FilterGroup,         
    IFilterBar 
} from '../../models/utilities/filter-bar';
import { ColumnOrder, ListColumn, SelectedSort } from '../../models/utilities/list-header';
import { PagedApiResult, PageData } from '../../models/utilities/paged-request';
import { GlobalConstants } from '../../utils/app-constants';
import { AppUtils } from '../../utils/app-utils';
import { toShortDate } from '../../utils/date-utils';
import { DeselectAll, SelectableGroup, SelectAll } from '../drag-select';
import { SelectedResetter } from '../drag-select/SelectableGroup';
import { FilterBar } from '../filter-bar/FilterBar';
import { EmptyTable } from '../tables/EmptyTable';
import { TablePager } from '../tables/TablePager';

// #region Component State and Props

interface IProps {
    rowRenderer                     : any;
    data                            : PagedApiResult<any>;
    filterProps                     : IFilterBar;
    selectedResetter?               : SelectedResetter;
    onFilterUpdate                  : any;
    searchCallback                  : any;
    noResultsMessage                : string;
    transparentBackground?          : boolean;
    id?                             : string;
    icon?                           : string;
    numberOfColumns?                : number;
    numberOfActions?                : number;
    rowClass?                       : string;
    reduxDispatch?                  : any;
    listTopper?                     : any; 
    serviceCenterOverride?          : string;
    guardCallback?                  : any;
    onDragComplete?                 : any;
    enabledDrag                     : boolean;
    skipScroll                      : boolean;
    onReset?                        : any;
    hidePager?                      : boolean;
    selectableClass                 : string;
    useClientSelectablePositioning? : boolean;
    hideFilterBar?                  : boolean;
    useHover?                       : boolean;
    showSelectAll                   : boolean;
}

// #endregion

const PagedList = (props: IProps) => {

    const [isInitialLoad, setIsInitialLoad] = React.useState(false);
    const [hasPendingFilters, setHasPendingFilters] = React.useState(false);

    // Fire off search when props change    
    React.useEffect(() => {
        if (!hasPendingFilters) {
            onSearch();
        }
    }, [props.filterProps]);

    /**
     * Handle the search command.  
     * Checks if there's a guard available.
     * If there is a guard, call it and wait on the result
     * before continuing.
     */
    const onSearch = async (overrideFilters?: IFilterBar) => {

        let canContinue = true;
        if (props.guardCallback) {
            canContinue = await props.guardCallback();
        }

        if (canContinue) {

            const tmp = updateAppliedFilters(overrideFilters);

            if (!tmp.paginationInfo.reusePaging) {
                tmp.paginationInfo = new PageData(GlobalConstants.DEFAULT_PAGE_SIZE);
            }
            else {
                tmp.paginationInfo.reusePaging = false;
            }

            props.searchCallback(tmp);
            scrollResults();

            if (overrideFilters === undefined) {
                setHasPendingFilters(false);
            }
            else {
                setHasPendingFilters(true);
            }

        }
    };

    /**
     * Check and update what filters are applied
     * @param overrideFilters Possible list of override filters to use instead of props
     */
    const updateAppliedFilters = (overrideFilters?: IFilterBar) => {

        if (overrideFilters && overrideFilters.hasOwnProperty('target')) {
            return props.filterProps;
        }

        const tmp = (overrideFilters) ? overrideFilters : props.filterProps;
        tmp.appliedFilters = [];

        if (tmp.filters) {
            tmp.filters.forEach((itm: FilterGroup) => {
                itm.opts.forEach((subitm: FilterBarInputOpt) => {
                    if ((itm.type === 'text' || itm.type === 'numeric' || itm.type === 'dealer') && subitm.data.length > 0) {
                        tmp.appliedFilters.push({
                            uuid: subitm.uuid,
                            label: `${itm.name} : ${subitm.data}`,
                            qsName: itm.qsParamName,
                            qsValue: subitm.data
                        });
                    }
                    else if (itm.type === 'autocomplete' && subitm.data) {
                        tmp.appliedFilters.push({
                            uuid: subitm.uuid,
                            label: `${itm.name} : ${subitm.data.name}`,
                            qsName: itm.qsParamName,
                            qsValue: subitm.data.id
                        });
                    }
                    else if (itm.type === 'checkboxes' && subitm.data === true) {
                        tmp.appliedFilters.push({
                            uuid: subitm.uuid,
                            label: `${itm.name} : ${subitm.label}`,
                            qsName: itm.qsParamName,
                            qsValue: subitm.qsValue
                        });
                    }
                    else if (itm.type === 'dropdown' && subitm.data) {
                        tmp.appliedFilters.push({
                            uuid: subitm.uuid,
                            label: `${itm.name} : ${subitm.label}`,
                            qsName: itm.qsParamName,
                            qsValue: subitm.qsValue
                        });
                    }
                    else if (itm.type === 'toggle' && subitm.data === true) {
                        tmp.appliedFilters.push({
                            uuid: subitm.uuid,
                            label: itm.name,
                            qsName: itm.qsParamName,
                            qsValue: 'true'
                        });
                    }
                    else if (itm.type === 'date' && subitm.data !== undefined) {
                        tmp.appliedFilters.push({
                            uuid: subitm.uuid,
                            label: `${itm.name} : ${toShortDate(subitm.data)}`,
                            qsName: itm.qsParamName,
                            qsValue: toShortDate(subitm.data)
                        });
                    }
                    else if (itm.type === 'dateplus' && subitm.data !== undefined && subitm.data.selectedDate !== undefined && subitm.data.selectedDate.qsValue !== undefined) {
                        tmp.appliedFilters.push({
                            uuid: subitm.uuid,
                            label: `${itm.name} : ${subitm.data.checkboxValue.qsValue === true ? 'DOW' : '<='} ${toShortDate(subitm.data.selectedDate.qsValue)}`,
                            qsName: itm.qsParamName,
                            qsValue: `${toShortDate(subitm.data.selectedDate.qsValue)}&${subitm.data.checkboxValue.qsName}=${subitm.data.checkboxValue.qsValue}`
                        });
                    }
                });
            });
        }

        return tmp;
    };

    /**
     * 
     * @param propName 
     * @param optId 
     * @param newState 
     * @param propType 
     */
    const onFilterUpdate = (propName: string, optId: string, newState: any, propType: FILTER_BAR_TYPE) => {

        const tmp = { ...props.filterProps } as IFilterBar;
        tmp.filters.forEach((itm: FilterGroup) => {
            if (itm.name === propName) {
                itm.opts.forEach((subitm: FilterBarInputOpt) => {
                    // For dropdown types, we only want one value to be selected at a time
                    if (propType === 'dropdown') {
                        subitm.data = (subitm.uuid === optId) ? newState : false;
                    }
                    else if (propType === 'dateplus') {
                        if (Object.prototype.toString.call(newState) === '[object Date]') {
                            subitm.data.selectedDate.qsValue = newState;
                        }
                        else {
                            subitm.data.checkboxValue.qsValue = newState;
                        }
                    }
                    else if (subitm.uuid === optId) {
                        subitm.data = newState;
                    }
                });
            }
        });

        setHasPendingFilters(true);
        props.onFilterUpdate(tmp);
    };

    const onFilterDelete = (uid: string) => {

        let tmp = { ...props.filterProps } as IFilterBar;

        tmp.filters.forEach((itm: FilterGroup) => {
            itm.opts.forEach((subitm: FilterBarInputOpt) => {
                if (subitm.uuid === uid) {
                    switch (itm.type) {
                        case 'text':
                        case 'dealer':
                        case 'autocomplete':
                        case 'numeric':
                            subitm.data = '';
                            break;
                        case 'checkboxes':
                        case 'dropdown':
                        case 'toggle':
                            subitm.data = false;
                            break;
                        case 'date':
                            subitm.data = undefined;
                            break;
                        case 'dateplus':
                            subitm.data.selectedDate = {
                                qsValue: undefined,
                                qsName: 'maxCutoff'
                            };
                            subitm.data.checkboxValue = {
                                qsValue: true,
                                qsName: 'matchDayOfWeek'
                            };
                            break;
                    }
                }
            });
        });

        tmp = findAndRemoveAppliedFilter(uid, tmp);

        if (tmp.appliedFilters.length === 0) {
            // tmp.clearAll();
        }

        onSearch(tmp);
    };

    const onFilterReset = () => {
        setHasPendingFilters(false);
        props.onReset();
    };

    const findAndRemoveAppliedFilter = (uid: string, filters: IFilterBar) => {

        const idx = filters.appliedFilters.findIndex((itm: AppliedFilterBarOpt) => {
            return itm.uuid === uid;
        });

        if (idx > -1) {
            filters.appliedFilters.splice(idx, 1);
        }

        return filters;
    };

    /**
     * Button handler for first click.  
     * Checks if there's a guard available.
     * If there is a guard, call it and wait on the result
     * before continuing.
     */
    const onFirstClick = async () => {

        let canContinue = true;
        if (props.guardCallback) {
            canContinue = await props.guardCallback();
        }

        if (canContinue) {
            firstClick();
        }
    };

    /**
     * Button handler for previous click.  
     * Checks if there's a guard available.
     * If there is a guard, call it and wait on the result
     * before continuing.
     */
    const onPreviousClick = async () => {

        let canContinue = true;
        if (props.guardCallback) {
            canContinue = await props.guardCallback();
        }

        if (canContinue) {
            previousClick();
        }
    };

    /**
     * Button handler for next click.  
     * Checks if there's a guard available.
     * If there is a guard, call it and wait on the result
     * before continuing.
     */
    const onNextClick = async () => {

        let canContinue = true;
        if (props.guardCallback) {
            canContinue = await props.guardCallback();
        }

        if (canContinue) {
            nextClick();
        }
    };

    /**
     * Button handler for last click.  
     * Checks if there's a guard available.
     * If there is a guard, call it and wait on the result
     * before continuing.
     */
    const onLastClick = async () => {

        let canContinue = true;
        if (props.guardCallback) {
            canContinue = await props.guardCallback();
        }

        if (canContinue) {
            lastClick();
        }
    };

    /**
     * Scroll the page back up to the given ID smoothly
     */
    const scrollResults = () => {
        if (props.skipScroll) {
            return;
        }

        if (isInitialLoad) {
            setIsInitialLoad(false);
            window.scroll({ top: 0, left: 0, behavior: 'smooth' });
        }
        else if (props.id && props.id.length > 0) {
            const ele = document.getElementById(props.id);
            if (ele) {
                const rect = ele.getBoundingClientRect();
                window.scroll({ top: (rect.top + window.scrollY - 20), left: 0, behavior: 'smooth' });
            }
        }
        else {
            window.scroll({ top: 0, left: 0, behavior: 'smooth' });
        }
    };

    /**
     * Adjust the paging info forward and call back to the component to search
     */
    const nextClick = () => {

        const paging = props.data.pagination;
        paging.goForward();
        props.searchCallback(props.filterProps, paging);
        scrollResults();
    };
    /**
     * Adjust the paging info to the last page and call back to the component to search
     */
    const lastClick = () => {

        const paging = props.data.pagination;
        paging.goEnd();
        props.searchCallback(props.filterProps, paging);
        scrollResults();
    };

    /**
     * Adjust the paging info back and call back to the component to search
     */
    const firstClick = () => {

        const paging = props.data.pagination;
        paging.goStart();
        props.searchCallback(props.filterProps, paging);
        scrollResults();
    };

    /**
     * Adjust the paging info back and call back to the component to search
     */
    const previousClick = () => {

        const paging = props.data.pagination;
        paging.goBack();
        props.searchCallback(props.filterProps, paging);
        scrollResults();
    };

    const onHeaderClick = (selectedColumn: ListColumn) => {

        if (selectedColumn.isSortable === false) {
            return;
        }

        let tmp = { ...props.filterProps } as IFilterBar;

        if (AppUtils.IsEmpty(tmp.selectedSort)) {
            tmp.selectedSort = new SelectedSort(selectedColumn.sortName, ColumnOrder.ASCENDING);
        }
        else {
            if (tmp.selectedSort.sortBy === selectedColumn.sortName) {
                tmp.selectedSort.sortDir = (tmp.selectedSort.sortDir === ColumnOrder.ASCENDING)
                    ? ColumnOrder.DESCENDING
                    : ColumnOrder.ASCENDING;
            }
            else {
                tmp.selectedSort = new SelectedSort(selectedColumn.sortName, ColumnOrder.ASCENDING);
            }
        }

        onSearch(tmp);
    };

    const getSortIcon = (itm: ListColumn) => {

        if (itm.isSortable) {
            const currentSort = props.filterProps.selectedSort;
            if (currentSort && currentSort.sortBy === itm.sortName) {
                return currentSort.sortDir === ColumnOrder.ASCENDING
                    ? <i className="la la-sort-up" />
                    : <i className="la la-sort-down" />;
            }
            else {
                return <i className="la la-sort" />;
            }

        }
        else {
            return null;
        }
    };

    const actionClass = (itm: ListColumn, index: number) => {
        let className = '';

        if (index === props.filterProps.columns.length - 1) {
            if (props.numberOfActions) {
                className += `buttons-${props.numberOfActions}`;
            }
        }

        if (props.filterProps.selectedSort && props.filterProps.selectedSort.sortBy === itm.sortName) {
            className += ' sorting-by';
        }

        if (itm.headerClass) {
            className += ` ${itm.headerClass}`;
        }

        return className;
    };

    const colCount = props.numberOfColumns ? props.numberOfColumns : props.filterProps.columns.length;
    const trueIcon = props.icon ? props.icon : 'la-search';
    const thRowClass = props.rowClass ? props.rowClass : '';

    return <React.Fragment>
        {
            !props.hideFilterBar
                ? <div className="row" id={props.id}>
                    <div className="col">
                        <FilterBar
                            onApplyFilters={onSearch}
                            onUpdateFilters={onFilterUpdate}
                            onFilterDelete={onFilterDelete}
                            onFilterReset={onFilterReset}
                            filterProps={props.filterProps}
                        />
                    </div>
                </div>
                : null
        }
        <div className="row">
            <div className="col-md-12">
                {
                    props.listTopper()
                }
            </div>
        </div>
        <div className="row">
            <div className="col-md-12">
                <SelectableGroup
                    className={`selectable-table ${props.selectableClass}`}
                    clickClassName="order-selectable"
                    enableDeselect
                    tolerance={0}
                    allowShiftClick={true}
                    selectOnClick={true}
                    globalMouse={true}
                    resetter={props.selectedResetter}
                    useClientPositioning={props.useClientSelectablePositioning}
                    ignoreList={['th', '.punchthrough', '.table-pager']}
                    disabled={!props.enabledDrag}
                    onSelectionFinish={props.onDragComplete}
                    allowClickWithoutSelected={true}>

                    {
                        (props.showSelectAll && props.data && props.data.results && props.data.results.length > 0)

                            ? <div className="pagedlist-selectalls">
                                <p>Select:</p>
                                <SelectAll>
                                    <button className="btn new action">All</button>
                                </SelectAll>
                                <DeselectAll>
                                    <button className="btn new action">None</button>
                                </DeselectAll>
                            </div>
                            : null
                    }

                    <table className={props.useHover ? "table table-hover" : "table"}>
                        {
                            props.filterProps.columns && props.filterProps.columns.length > 0
                                ? <thead>
                                    <tr className={thRowClass}>
                                        {
                                            props.filterProps.columns.map((itm: ListColumn, idx: number) => (
                                                <th
                                                    className={actionClass(itm, idx)}
                                                    onClick={() => onHeaderClick(itm)}
                                                    key={`sortIdx-${idx}-${itm.sortName}`}>
                                                    {itm.display}
                                                    {
                                                        getSortIcon(itm)
                                                    }
                                                </th>
                                            ))
                                        }
                                    </tr>
                                </thead>
                                : null
                        }
                        <tbody>
                            {
                                (props.data && props.data.results && props.data.results.length > 0)
                                    ? props.data.results.map((itm: any) => (
                                        props.rowRenderer(itm)
                                    ))
                                    : <EmptyTable
                                        transparent={props.transparentBackground}
                                        icon={trueIcon}
                                        columns={colCount}
                                        message={props.noResultsMessage} />
                            }
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={colCount}>
                                    {
                                        props.data && !props.hidePager
                                            ? <TablePager
                                                pageData={props.data.pagination!}
                                                onFirstClick={onFirstClick}
                                                onPrevClick={onPreviousClick}
                                                onNextClick={onNextClick}
                                                onLastClick={onLastClick} />
                                            : null
                                    }
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </SelectableGroup>
            </div>
        </div>
    </React.Fragment>;

};

PagedList.defaultProps = {        
    enabledDrag             : false,
    hidePager               : false,
    icon                    : '',
    numberOfActions         : 0,
    numberOfColumns         : 0,
    onDragComplete          : undefined,
    rowClass                : '',
    serviceCenterOverride   : undefined,
    skipScroll              : false,
    transparentBackground   : false,        
    reduxDispatch           : () => { return; },
    listTopper              : () => { return; },
    pagingCallback          : () => { return; },
    hideFilterBar           : false,
    useHover                : true,
    showSelectAll           : false
};

export { PagedList };
